import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './RecoverySheet.module.css';

/** Class on `<body>` while the recovery sheet prints: print styles of other pages stay untouched. */
const PRINTING_CLASS = 'recovery-printing';
/**
 * Browsers differ in whether and when `afterprint` fires (it can be missing when the dialog is
 * cancelled, notably on mobile). If the page is not in print layout this long after `print()`, the
 * print is over.
 */
export const PRINT_FALLBACK_MS = 10_000;

interface RecoverySheetProps {
  readonly words: readonly string[];
  /** First 8 characters of the space identifier; null while it is still being derived. */
  readonly spaceIdPrefix: string | null;
  /** Date of printing, already formatted. */
  readonly dateText: string;
  /** Replaces the passphrase sheet title and warning (the account recovery code uses its own). */
  readonly title?: string;
  readonly warning?: string;
  /** Called once the browser's print dialog has closed. */
  readonly onDone: () => void;
}

/**
 * The printable recovery sheet: the 12 words, the space identifier prefix, the date and a
 * warning. It is mounted only while printing; on screen it is hidden, and on paper it is the
 * only thing printed (the app is hidden by a rule scoped to the `recovery-printing` body class).
 */
export function RecoverySheet({
  words,
  spaceIdPrefix,
  dateText,
  title,
  warning,
  onDone,
}: RecoverySheetProps) {
  const { t } = useTranslation();
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  });

  useEffect(() => {
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      done.current();
    };
    // The print layout ending (media query `print` stops matching) is a second signal of the same event.
    const printLayout = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null;
    const onLayoutChange = (event: { matches: boolean }): void => {
      if (!event.matches) finish();
    };
    document.body.classList.add(PRINTING_CLASS);
    window.addEventListener('afterprint', finish);
    printLayout?.addEventListener('change', onLayoutChange);
    window.print();
    const fallback = window.setTimeout(() => {
      if (printLayout?.matches !== true) finish();
    }, PRINT_FALLBACK_MS);
    return () => {
      window.clearTimeout(fallback);
      window.removeEventListener('afterprint', finish);
      printLayout?.removeEventListener('change', onLayoutChange);
      document.body.classList.remove(PRINTING_CLASS);
    };
  }, []);

  return createPortal(
    <section className={styles.sheet} data-testid="recovery-sheet" aria-hidden="true">
      <h1>{title ?? t('sync.recovery.title')}</h1>
      <ol className={styles.words}>
        {words.map((word, index) => (
          <li key={index}>
            <span className={styles.number}>{index + 1}</span>
            <span>{word}</span>
          </li>
        ))}
      </ol>
      <dl className={styles.meta}>
        <dt>{t('sync.recovery.space')}</dt>
        <dd>{spaceIdPrefix ?? '—'}</dd>
        <dt>{t('sync.recovery.date')}</dt>
        <dd>{dateText}</dd>
      </dl>
      <p className={styles.warning}>{warning ?? t('sync.recovery.warning')}</p>
    </section>,
    document.body,
  );
}
